import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { withTranslation } from '../../ChildrenContext.js';
import PropTypes from 'prop-types';
import CustomModal from '../../Modal';
import FormGroup from '../../FormGroup';
import template from '../../../helpers/template';
import CustomNotify from "../../Notify";
import config, { statuses, metaUtmParams } from "../../../config";
import Icon from '../../Icon';
import CustomTooltip from '../../Tooltip';
import ls from '../../../helpers/localStorage';

const qrCode = {
    qr: require('shared_img/qr-code.svg')
};

const META_PARAMS_STORAGE_KEY = 'linkConstructorLastMetaParams';
const DEFAULT_QR_SIZE = 320;
const META_KEYS_TO_STORE = [
    ...(Array.isArray(metaUtmParams) ? metaUtmParams : []),
    ...Array.from(Array(config.metaParamsCount || 0).keys()).map(i => `param${i + 1}`)
];

const LinkConstructor = ({
                             creativeType,
                             creativeId,
                             linkId,
                             visible,
                             goalEntries,
                             publisherId,
                             publisherIsReferral,
                             offer,
                             goalEntryId,
                             type,
                             codeParams,
                             access,
                             onClose,
                             getDataLink,
                             getShortUrl,
                             getShortenerDomainList,
                             getQRCode,
                             saveLinkPresets,
                             onSave: propOnSave,
                             t
                         }) => {
    const staticNames = (t && t.constructors) || {};
    const referralBadges = (t && t.referral) || {};

    const [state, setState] = useState({
        creativeType: creativeType || 'link',
        creativeId: creativeId || linkId,
        linkId: linkId,
        modalIsVisible: visible,
        goalEntries: goalEntries,
        publisherId: publisherId,
        publisherIsReferral: publisherIsReferral,
        link: {},
        metaParams: {},
        metaParamsmetaParams: {},
        selfieLink: '',
        currentLinkId: '',
        resultLink: '',
        type: type,
        codeParams: codeParams || {},
        ttl: 30,
        qrCodeColors: {
            colorRed: '',
            colorGreen: '',
            colorBlue: '',
            bgColorRed: '',
            bgColorGreen: '',
            bgColorBlue: ''
        },
        qrCodeSize: 320,
        qrCodeType: getQRCodeTypes()[0].value,
        qrDownloadContent: staticNames.qrCodeDownload,
        qrCodeContent: `<img class="qr_default" src="${qrCode.qr}" alt=""/>`,
        shortenerDomainList: [],
        domain: '',
        shortUrl: '',
        modalOptions: {
            link: {
                title: staticNames.createLink,
                pattern: '{{ link }}'
            },
            container: {
                title: staticNames.createContainer,
                pattern: '<iframe src="{{ link }}" width="{{ width }}" height="{{ height }}" align="middle">{{ text }}</iframe>'
            }
        },
        saveLinkPresets: saveLinkPresets
    });

    const resultRef = useRef(null);
    const shortLinkRef = useRef(null);
    const prevPublisherIdRef = useRef(publisherId);
    const prevModalVisibleRef = useRef(visible);
    const domains = getShortenerDomains();

    function getShortenerDomains() {
        const domains = state.shortenerDomainList;
        let listDomains = [];

        domains.forEach(function(domain) {
            listDomains.push({title: domain, value: domain});
        });

        return listDomains;
    }

    function getQRCodeTypes() {
        const types = config.qrCodeTypes;
        let listTypes = [];

        types.forEach(function(type) {
            listTypes.push({title: type.toUpperCase(), value: type});
        });

        return listTypes;
    }

    const getFirstGoal = useCallback(() => {
        if (state.goalEntries && state.goalEntries.length > 0) {
            return state.goalEntries[0].id;
        }
        return null;
    }, [state.goalEntries]);

    const getAllowChar = useCallback(() => {
        return /[^\da-zA-Z.%_{}\[\]#$\-]/g;
    }, []);

    const initLink = useCallback(() => {
        if (!state.linkId) {
            return;
        }
        getDataLink(state.linkId, successLink);
    }, [state.linkId, getDataLink]);

    const successLink = useCallback((data) => {
        // Parse params safely
        let parsedParams = {};
        if (data && data.params) {
            try {
                let linkParams = typeof data.params === "string" ? (data.params.trim() ? JSON.parse(data.params) : {}) : data.params;
                if (linkParams && typeof linkParams === 'object' && linkParams !== null) {
                    parsedParams = linkParams;
                }
            } catch (e) {
                console.error('Failed to parse link params:', e);
                parsedParams = {};
            }
        }

        const filterAllowedMeta = (source = {}) => {
            if (!source || typeof source !== 'object') {
                return {};
            }
            return META_KEYS_TO_STORE.reduce((acc, key) => {
                if (source[key] !== undefined) {
                    acc[key] = source[key];
                }
                return acc;
            }, {});
        };

        const parseStoredEntry = (stored) => {
            if (!stored || typeof stored !== 'object') {
                return { params: {}, hasEntry: false };
            }
            if (stored.params && typeof stored.params === 'object') {
                return {
                    params: filterAllowedMeta(stored.params),
                    hasEntry: true
                };
            }
            const params = filterAllowedMeta(stored);
            const hasEntry = Object.keys(stored).length > 0;
            return { params, hasEntry };
        };

        const storageKey = `${META_PARAMS_STORAGE_KEY}:${publisherId || 'unknown'}`;
        const storedEntry = parseStoredEntry(
            typeof window !== 'undefined' ? ls.get(storageKey) : {}
        );
        const storedMetaParams = storedEntry.params || {};
        const parsedAllowed = filterAllowedMeta(parsedParams);
        const nonMetaParsed = Object.keys(parsedParams || {}).reduce((acc, key) => {
            if (!META_KEYS_TO_STORE.includes(key)) {
                acc[key] = parsedParams[key];
            }
            return acc;
        }, {});
        const hasParsedParams = Object.keys(parsedAllowed).length > 0;
        const hasStoredParams = storedEntry.hasEntry;

        setState(prevState => ({
            ...prevState,
            link: {
                ...data,
                params: hasStoredParams
                    ? {
                        ...nonMetaParsed,
                        ...storedMetaParams
                    }
                    : (hasParsedParams
                        ? parsedParams
                        : (prevState.link?.params || null))
            },
            metaParams: hasStoredParams
                ? storedMetaParams
                : (hasParsedParams ? parsedAllowed : prevState.metaParams),
            qrCodeColors: {
                colorRed: '',
                colorGreen: '',
                colorBlue: '',
                bgColorRed: '',
                bgColorGreen: '',
                bgColorBlue: ''
            },
            qrCodeSize: 320,
            qrCodeType: getQRCodeTypes()[0].value,
            qrDownloadContent: staticNames.qrCodeDownload,
            qrCodeContent: `<img class="qr_default" src="${qrCode.qr}" alt=""/>`,
            domain: domains.length ? domains[0].value : '',
            shortUrl: ''
        }));
    }, [staticNames, domains, publisherId]);

    const getMetaParams = useCallback(() => {
        let metaParams = Object.keys(state.metaParams).map((paramKey) => {
            let value = state.metaParams[paramKey];
            return value ? paramKey + '=' + value : ''
        });

        metaParams = metaParams.filter(n => {
            return n && n !== '&'
        });
        return metaParams.length ? '&' + metaParams.join('&') : '';
    }, [state.metaParams]);

    const getLinkParams = useCallback(() => {
        let linkIdParam = '?l=' + state.linkId,
            selfieLink = state.selfieLink;

        return selfieLink ? linkIdParam + '&sl=' + encodeURIComponent(selfieLink) : linkIdParam;
    }, [state.linkId, state.selfieLink]);

    const getResultLink = useCallback((linkObj, publisherIsReferral = false) => {
        if (!offer || !offer.tds_domain || !state.publisherId || !state.goalEntryId) {
            return '';
        }

        let metaParams = getMetaParams();

        if (linkObj && linkObj.params) {
            metaParams = '';
            try {
                let linkParams = typeof linkObj.params === "string" ? (linkObj.params.trim() ? JSON.parse(linkObj.params) : {}) : linkObj.params;

                if (linkParams !== undefined && linkParams !== null && typeof linkParams === 'object' && Object.keys(linkParams).length > 0) {
                    Object.keys(linkParams).forEach((key) => {
                        const value = linkParams[key];
                        if (value && typeof value === 'string' && value.trim().length > 0) {
                            metaParams = metaParams + '&' + key + '=' + value;
                        }
                    });
                }
            } catch (e) {
                console.error('Failed to parse linkObj.params:', e);
            }
        }

        let advertiserIsReferral = false,
            offerIsReferral = false,
            currentGoalEntryId = state.goalEntryId;
        if (currentGoalEntryId && state.goalEntries && state.goalEntries.length) {
            state.goalEntries.forEach(function (goalEntry) {
                if (goalEntry.id === currentGoalEntryId) {
                    advertiserIsReferral = goalEntry.offer.advertiser.referral_advertiser;
                    offerIsReferral = goalEntry.offer.referral_offer;
                }
            });
        }

        metaParams = metaParams + '&creative_type=' + state.creativeType + '&creative_id=' + state.creativeId;

        if (publisherIsReferral && advertiserIsReferral && offerIsReferral) {
            metaParams += '&param8=referer_id';
        }

        return `${offer.tds_domain}/${state.publisherId}/${state.goalEntryId}${getLinkParams()}${metaParams}`;
    }, [state, getMetaParams, getLinkParams, offer]);

    const getCode = useCallback(() => {
        if(!state.modalOptions[state.type]) {
            return '';
        }

        let code = state.modalOptions[state.type].pattern;
        let params = {
            ...state.codeParams,
            link: getResultLink(state.link, state.publisherIsReferral)
        };

        return template(code, params);
    }, [state.modalOptions, state.type, state.codeParams, state.link, state.publisherIsReferral, getResultLink]);

    const setParam = useCallback((e) => {
        if (e.target.value.replace(getAllowChar(), '') !== e.target.value) {
            CustomNotify(staticNames.notAllowCharMsg, 'error');
            return;
        }

        const placeholder = e.target.placeholder || '';
        const inputValue = e.target.value || '';

        setState(prevState => {
            const linkParams = normalizeParams(prevState.link && prevState.link.params);
            const nonMetaParams = removeAllowedMetaKeys(linkParams);
            const baseMetaParams = {
                ...pickAllowedMetaKeys(linkParams),
                ...prevState.metaParams
            };
            let metaParams = { ...baseMetaParams };
            let link = { ...prevState.link };

            if(typeof link.params === "string") {
                try {
                    link.params = link.params.trim() ? JSON.parse(link.params) : {};
                } catch (e) {
                    console.error('Failed to parse link.params:', e);
                    link.params = {};
                }
            }

            if (inputValue && inputValue.trim().length > 0) {
                metaParams[placeholder] = inputValue;
            } else {
                delete metaParams[placeholder];
            }

            if(!link.params) {
                link['params'] = {};
            }

            link.params = {
                ...nonMetaParams,
                ...metaParams
            };

            persistMetaParams(metaParams);

            return {
                ...prevState,
                metaParams: metaParams,
                link: link
            };
        });
    }, [getAllowChar, staticNames, persistMetaParams, normalizeParams, pickAllowedMetaKeys, removeAllowedMetaKeys]);

    const getShortUrlHandler = useCallback((e) => {
        e.preventDefault();

        const code = getCode();
        if (!code || !code.trim()) {
            CustomNotify(staticNames.cantCreateUrlForCreativeNoLinksAvailable || 'Please generate a link first', 'error');
            return;
        }

        let params = {
            url: code,
            ttl: state.ttl || 30,
            domain: state.domain || domains[0]?.value
        };

        if (!params.domain) {
            CustomNotify(staticNames.errorDomainNotSelected || 'Please select a domain', 'error');
            return;
        }

        getShortUrl(params, successShortUrl);
    }, [domains, getCode, state.ttl, state.domain, getShortUrl, successShortUrl, staticNames]);

    const getShortenerDomainListHandler = useCallback(() => {
        let params = {};
        getShortenerDomainList(params, successShortenerDomainList);
    }, [getShortenerDomainList, successShortenerDomainList]);

    const getQRCodeHandler = useCallback(() => {
        let params = {
            url: state.shortUrl,
            size: parseInt(state.qrCodeSize),
            type: state.qrCodeType,
            colors: JSON.stringify({
                colorR: state.qrCodeColors.colorRed ? parseInt(state.qrCodeColors.colorRed) : 0,
                colorG: state.qrCodeColors.colorGreen ? parseInt(state.qrCodeColors.colorGreen) : 0,
                colorB: state.qrCodeColors.colorBlue ? parseInt(state.qrCodeColors.colorBlue) : 0,
                backgroundR: state.qrCodeColors.bgColorRed ? parseInt(state.qrCodeColors.bgColorRed) : 255,
                backgroundG: state.qrCodeColors.bgColorGreen ? parseInt(state.qrCodeColors.bgColorGreen) : 255,
                backgroundB: state.qrCodeColors.bgColorBlue ? parseInt(state.qrCodeColors.bgColorBlue) : 255
            })
        };

        getQRCode(params, successQRCode);
    }, [state.shortUrl, state.qrCodeSize, state.qrCodeType, state.qrCodeColors, getQRCode]);

    const successShortUrl = useCallback((url) => {
        setState(prevState => ({
            ...prevState,
            shortUrl: url
        }));
    }, []);

    const successShortenerDomainList = useCallback((list) => {
        setState(prevState => ({
            ...prevState,
            shortenerDomainList: list
        }));
    }, []);

    const successQRCode = useCallback((qrCode) => {
        const qrCodeDownloadEl = document.getElementById('qrCodeDownload');
        if (qrCodeDownloadEl) {
            qrCodeDownloadEl.className = 'btn_qr-download';
        }

        const qrImg = document.getElementById('qr-img');
        if (qrImg) {
            qrImg.style.filter = '';
        }

        setState(prevState => ({
            ...prevState,
            qrCodeContent: `<img id="qr-img" src="data:${qrCode.mime};base64,${qrCode.qr}" alt="QR Code" width="${prevState.qrCodeSize}px" height="${prevState.qrCodeSize}px">`,
            qrDownloadContent: `<a id="qr-download_link" href="data:${qrCode.mime};base64,${qrCode.qr}" download="qrCode.${prevState.qrCodeType}">${staticNames.qrCodeDownload}</a>`
        }));
    }, [staticNames]);

    const handleTtlChange = useCallback((e) => {
        const value = e.target.value;

        if (/[^0-9]/.test(value) || parseInt(e.target.value) >= 1000) {
            return;
        }

        setState(prevState => ({
            ...prevState,
            ttl: e.target.value
        }));
    }, []);

    const onSelectChange = useCallback((e) => {
        setState(prevState => ({
            ...prevState,
            domain: e.target.value
        }));
    }, []);

    const unsecuredCopyToClipboard = useCallback(() => {
        var elem = document.getElementById('short-link_clip');
        if (elem) {
            elem.select();

            try {
                document.execCommand("copy");
                CustomNotify(elem.value);
            } catch(err){
                console.error('Unable to copy to clipboard', err);
            }
        }
    }, []);

    const copyToClipboard = useCallback(() => {
        if (window.isSecureContext && navigator.clipboard) {
            navigator.clipboard.writeText(state.shortUrl);
            CustomNotify(state.shortUrl);
        } else {
            unsecuredCopyToClipboard();
        }
    }, [state.shortUrl, unsecuredCopyToClipboard]);

    const qrColorChangeTimeoutRef = useRef(null);
    const stateRef = useRef(state);
    const getQRCodeRef = useRef(getQRCode);
    const successQRCodeRef = useRef(successQRCode);
    const storageIsAvailable = typeof window !== 'undefined' && window.localStorage;
    const updateQrSizeOutput = useCallback((size) => {
        const slider = document.getElementById('sizeRange');
        const qrSizeOutput = document.getElementById('sizeValue');
        if (!slider || !qrSizeOutput) {
            return;
        }

        const min = Number(slider.min) || 0;
        const max = Number(slider.max) || 1;
        const val = Number(size) || min;

        const percent = (val - min) / (max - min);
        const sliderWidth = slider.offsetWidth || slider.clientWidth || (slider.getBoundingClientRect && slider.getBoundingClientRect().width) || 0;
        const thumbWidth = 16;
        const left = percent * (sliderWidth - thumbWidth) + thumbWidth / 2;

        qrSizeOutput.style.left = `${left}px`;
        qrSizeOutput.textContent = val;
    }, []);

    const normalizeParams = useCallback((params) => {
        if (!params) {
            return {};
        }

        try {
            return typeof params === 'string'
                ? (params.trim() ? JSON.parse(params) : {})
                : (typeof params === 'object' ? params : {});
        } catch (e) {
            console.error('Failed to parse params for storage:', e);
            return {};
        }
    }, []);

    const pickAllowedMetaKeys = useCallback((source = {}) => {
        if (!source || typeof source !== 'object') {
            return {};
        }
        return META_KEYS_TO_STORE.reduce((acc, key) => {
            if (source[key] !== undefined) {
                acc[key] = source[key];
            }
            return acc;
        }, {});
    }, []);

    const removeAllowedMetaKeys = useCallback((source = {}) => {
        if (!source || typeof source !== 'object') {
            return {};
        }
        return Object.keys(source).reduce((acc, key) => {
            if (!META_KEYS_TO_STORE.includes(key)) {
                acc[key] = source[key];
            }
            return acc;
        }, {});
    }, []);

    const hasAllowedMetaParams = useCallback((params = {}) => {
        if (!params || typeof params !== 'object') {
            return false;
        }

        return META_KEYS_TO_STORE.some((key) => {
            const value = params[key];
            return value !== undefined && value !== null && String(value).length > 0;
        });
    }, []);

    const getMetaStorageKey = useCallback(() => {
        return `${META_PARAMS_STORAGE_KEY}:${publisherId || 'unknown'}`;
    }, [publisherId]);

    const parseStoredMetaEntry = useCallback((stored) => {
        if (!stored || typeof stored !== 'object') {
            return { params: {}, hasEntry: false };
        }

        if (stored.params && typeof stored.params === 'object') {
            return {
                params: pickAllowedMetaKeys(stored.params),
                hasEntry: true
            };
        }

        const params = pickAllowedMetaKeys(stored);
        const hasEntry = Object.keys(stored).length > 0;
        return { params, hasEntry };
    }, [pickAllowedMetaKeys]);

    const getStoredMetaEntryByKey = useCallback((key) => {
        if (!storageIsAvailable) {
            return { params: {}, hasEntry: false };
        }

        const stored = ls.get(key);
        return parseStoredMetaEntry(stored);
    }, [storageIsAvailable, parseStoredMetaEntry]);

    const getStoredMetaEntry = useCallback(() => {
        const primaryKey = getMetaStorageKey();
        const primaryEntry = getStoredMetaEntryByKey(primaryKey);

        if (publisherId && !primaryEntry.hasEntry) {
            const fallbackKey = `${META_PARAMS_STORAGE_KEY}:unknown`;
            const fallbackEntry = getStoredMetaEntryByKey(fallbackKey);

            if (fallbackEntry.hasEntry) {
                try {
                    ls.set(primaryKey, {
                        params: fallbackEntry.params,
                        updatedAt: Date.now()
                    });
                    ls.set(fallbackKey, {});
                } catch (e) {
                    console.error('Failed to migrate meta params storage:', e);
                }

                return fallbackEntry;
            }
        }

        return primaryEntry;
    }, [getMetaStorageKey, getStoredMetaEntryByKey, publisherId]);

    const readStoredMetaParams = useCallback(() => {
        return getStoredMetaEntry().params;
    }, [getStoredMetaEntry]);

    const clearStoredMetaParams = useCallback(() => {
        if (!storageIsAvailable) {
            return;
        }
        try {
            ls.set(getMetaStorageKey(), { params: {}, updatedAt: Date.now() });
        } catch (e) {
            console.error('Failed to clear meta params:', e);
        }
    }, [storageIsAvailable, getMetaStorageKey]);

    const persistMetaParams = useCallback((metaParams) => {
        if (!storageIsAvailable || !publisherId) {
            return;
        }

        try {
            const prepared = pickAllowedMetaKeys(metaParams || {});
            ls.set(getMetaStorageKey(), {
                params: prepared,
                updatedAt: Date.now()
            });
        } catch (e) {
            console.error('Failed to persist meta params:', e);
        }
    }, [storageIsAvailable, pickAllowedMetaKeys, getMetaStorageKey, publisherId]);

    // QR settings are not persisted by request.

    useEffect(() => {
        stateRef.current = state;
        getQRCodeRef.current = getQRCode;
        successQRCodeRef.current = successQRCode;
    }, [state, getQRCode, successQRCode]);

    const debouncedQRCodeColorUpdate = useCallback(() => {
        if (qrColorChangeTimeoutRef.current) {
            clearTimeout(qrColorChangeTimeoutRef.current);
        }

        qrColorChangeTimeoutRef.current = setTimeout(() => {
            if (stateRef.current.shortUrl.length >= 4) {
                const params = {
                    url: stateRef.current.shortUrl,
                    size: parseInt(stateRef.current.qrCodeSize),
                    type: stateRef.current.qrCodeType,
                    colors: JSON.stringify({
                        colorR: stateRef.current.qrCodeColors.colorRed ? parseInt(stateRef.current.qrCodeColors.colorRed) : 0,
                        colorG: stateRef.current.qrCodeColors.colorGreen ? parseInt(stateRef.current.qrCodeColors.colorGreen) : 0,
                        colorB: stateRef.current.qrCodeColors.colorBlue ? parseInt(stateRef.current.qrCodeColors.colorBlue) : 0,
                        backgroundR: stateRef.current.qrCodeColors.bgColorRed ? parseInt(stateRef.current.qrCodeColors.bgColorRed) : 255,
                        backgroundG: stateRef.current.qrCodeColors.bgColorGreen ? parseInt(stateRef.current.qrCodeColors.bgColorGreen) : 255,
                        backgroundB: stateRef.current.qrCodeColors.bgColorBlue ? parseInt(stateRef.current.qrCodeColors.bgColorBlue) : 255
                    })
                };
                getQRCodeRef.current(params, successQRCodeRef.current);
            }
        }, 500);
    }, []);

    const handleQRSizeChange = useCallback((e) => {
        const newSize = parseInt(e.target.value, 10) || DEFAULT_QR_SIZE;

        const qrImg = document.getElementById('qr-img');
        if (qrImg) {
            qrImg.style.width = newSize + 'px';
            qrImg.style.height = newSize + 'px';
        }

        updateQrSizeOutput(newSize);
        setState(prevState => ({
            ...prevState,
            qrCodeSize: newSize
        }));
    }, [updateQrSizeOutput]);

    const getQRSizeChange = useCallback(() => {
        if (stateRef.current.shortUrl.length >= 4) {
            const currentSize = parseInt(stateRef.current.qrCodeSize);
            const params = {
                url: stateRef.current.shortUrl,
                size: currentSize,
                type: stateRef.current.qrCodeType,
                colors: JSON.stringify({
                    colorR: stateRef.current.qrCodeColors.colorRed ? parseInt(stateRef.current.qrCodeColors.colorRed) : 0,
                    colorG: stateRef.current.qrCodeColors.colorGreen ? parseInt(stateRef.current.qrCodeColors.colorGreen) : 0,
                    colorB: stateRef.current.qrCodeColors.colorBlue ? parseInt(stateRef.current.qrCodeColors.colorBlue) : 0,
                    backgroundR: stateRef.current.qrCodeColors.bgColorRed ? parseInt(stateRef.current.qrCodeColors.bgColorRed) : 255,
                    backgroundG: stateRef.current.qrCodeColors.bgColorGreen ? parseInt(stateRef.current.qrCodeColors.bgColorGreen) : 255,
                    backgroundB: stateRef.current.qrCodeColors.bgColorBlue ? parseInt(stateRef.current.qrCodeColors.bgColorBlue) : 255
                })
            };

            const successCallback = (qrCode) => {
                const qrCodeDownloadEl = document.getElementById('qrCodeDownload');
                if (qrCodeDownloadEl) {
                    qrCodeDownloadEl.className = 'btn_qr-download';
                }

                const qrImg = document.getElementById('qr-img');
                if (qrImg) {
                    qrImg.style.filter = '';
                    qrImg.style.width = currentSize + 'px';
                    qrImg.style.height = currentSize + 'px';
                }

                setState(prevState => ({
                    ...prevState,
                    qrCodeContent: `<img id="qr-img" src="data:${qrCode.mime};base64,${qrCode.qr}" alt="QR Code" width="${currentSize}px" height="${currentSize}px">`,
                    qrDownloadContent: `<a id="qr-download_link" href="data:${qrCode.mime};base64,${qrCode.qr}" download="qrCode.${stateRef.current.qrCodeType}">${staticNames.qrCodeDownload}</a>`
                }));
            };

            getQRCodeRef.current(params, successCallback);
        }
    }, [staticNames]);

    const handleQRTypeChange = useCallback((e) => {
        let val = e.target.value;
        setState(prevState => ({ ...prevState, qrCodeType: val }));
    }, []);

    const handleQRColorChange = useCallback((e) => {
        let colorName = e.target.name;
        let val = e.target.value;

        if(val < 0) {
            val = 0;
        } else if (val > 255) {
            val = 255;
        }

        setState(prevState => ({
            ...prevState,
            qrCodeColors: {
                ...prevState.qrCodeColors,
                [colorName]: val
            }
        }));

        debouncedQRCodeColorUpdate();
    }, [debouncedQRCodeColorUpdate]);

    const renderBadge = useCallback(() => {
        let advertiserIsReferral = false,
            offerIsReferral = false,
            currentGoalEntryId = state.goalEntryId;

        if (currentGoalEntryId && state.goalEntries.length) {
            state.goalEntries.forEach(function(goalEntry) {
                if (goalEntry.id === currentGoalEntryId) {
                    advertiserIsReferral = goalEntry.offer.advertiser.referral_advertiser;
                    offerIsReferral = goalEntry.offer.referral_offer;
                }
            });
        }

        if (state.publisherIsReferral && advertiserIsReferral && offerIsReferral) {
            return (
                <span className="badge badge-danger ml10" title={referralBadges.link}>RL</span>
            )
        }
        return null;
    }, [state.publisherIsReferral, state.goalEntryId, state.goalEntries, referralBadges]);

    const getStatusIcon = useCallback(() => {
        if (!state.goalEntries || !Array.isArray(state.goalEntries) || state.goalEntries.length === 0) {
            return null;
        }

        const dataGoal = state.goalEntries.find((item) => item.id === state.goalEntryId) || {};

        return dataGoal.status === statuses.paused
            ? (
                <CustomTooltip id={`icon-paused-constructor`} tooltip={(t && t.offersPage && t.offersPage.offer && t.offersPage.offer.paused) || ''}>
                    <Icon name={`link-construrtor__label-icon fa fa-pause-circle`} />
                </CustomTooltip>
            )
            : null;
    }, [state.goalEntries, state.goalEntryId, t]);

    const getMetaUtmParamValue = useCallback((promoType, key) => {
        if (state.link && state.link.params) {
            try {
                let linkParams = typeof state.link.params === "string" ? (state.link.params.trim() ? JSON.parse(state.link.params) : {}) : state.link.params;
                if (linkParams && linkParams[key]) {
                    return linkParams[key];
                }
            } catch (e) {
                console.error('Failed to parse link.params in getMetaUtmParamValue:', e);
            }
        }

        if (state.metaParams && state.metaParams[key]) {
            return state.metaParams[key];
        }

        return '';
    }, [state.link, state.metaParams]);

    const getMetaParamValue = useCallback((promoType, index) => {
        if (state.link && state.link.params) {
            try {
                let linkParams = typeof state.link.params === "string" ? (state.link.params.trim() ? JSON.parse(state.link.params) : {}) : state.link.params;
                if (linkParams && linkParams['param' + (index + 1)]) {
                    return linkParams['param' + (index + 1)];
                }
            } catch (e) {
                console.error('Failed to parse link.params in getMetaParamValue:', e);
            }
        }

        if (state.metaParams && state.metaParams['param' + (index + 1)]) {
            return state.metaParams['param' + (index + 1)];
        }

        return '';
    }, [state.link, state.metaParams]);

    const hasAnyParams = useCallback(() => {
        if (state.selfieLink) {
            return true;
        }

        if (Object.keys(state.metaParams || {}).length > 0) {
            return true;
        }

        if (state.link && state.link.params) {
            try {
                let linkParams = typeof state.link.params === "string" ? (state.link.params.trim() ? JSON.parse(state.link.params) : {}) : state.link.params;
                if (linkParams && typeof linkParams === 'object' && linkParams !== null) {
                    const hasUtmParams = metaUtmParams && metaUtmParams.some(key => linkParams[key] && linkParams[key].length > 0);
                    if (hasUtmParams) {
                        return true;
                    }

                    const hasSubParams = config.metaParamsCount > 0 && Array.from(Array(config.metaParamsCount).keys()).some(index => {
                        const paramKey = 'param' + (index + 1);
                        return linkParams[paramKey] && linkParams[paramKey].length > 0;
                    });
                    if (hasSubParams) {
                        return true;
                    }
                }
            } catch (e) {
                console.error('Failed to parse link.params in hasAnyParams:', e);
            }
        }

        return false;
    }, [state.link, state.metaParams, state.selfieLink]);

    const renderGoalEntries = useCallback(() => {
        const goalEntries = state.goalEntries || [];

        return (
            <div className="form-group">
                <label className="control-label">{(t && t.constructors && t.constructors.goal) || ''}</label>
                {getStatusIcon()}
                <select onChange={onGoalChange}
                        value={state.goalEntryId || ''}
                        className="form-control"
                >
                    {
                        Array.isArray(goalEntries) && goalEntries.length > 0
                            ? goalEntries.map((goal, key) => {
                                return <option key={key} disabled={!goal.access_granted} value={goal.id}>{goal.goal.title}</option>
                            })
                            : <option value="">No goals available</option>
                    }
                </select>
            </div>
        );
    }, [t, getStatusIcon, onGoalChange, state.goalEntryId, state.goalEntries]);

    const renderParams = useCallback((promoType) => {
        if (!metaUtmParams || !Array.isArray(metaUtmParams) || metaUtmParams.length === 0) {
            return null;
        }

        return (
            <div className="form-group">
                <label className="control-label bold">{(t && t.constructors && t.constructors.utm) || ''}</label>
                <div className="form-row">
                    {metaUtmParams.map(key => {
                        let value = getMetaUtmParamValue(promoType, key);
                        return (
                            <input
                                type="text"
                                maxLength="50"
                                onChange={setParam}
                                placeholder={key}
                                className="form-control"
                                value={value}
                                key={key}
                            />
                        )
                    })}
                </div>
            </div>
        );
    }, [t, state.metaParams, setParam, getMetaUtmParamValue]);

    const renderSubMetaParams = useCallback((promoType) => {
        if (!config.metaParamsCount || config.metaParamsCount <= 0) {
            return null;
        }

        return (
            <div className="form-group">
                <label className="control-label bold">{(t && t.constructors && t.constructors.subaccount) || ''}</label>
                <div className="form-row">
                    {Array.from(Array(config.metaParamsCount).keys()).map((index) => {
                        let value = getMetaParamValue(promoType, index);
                        return (
                            <input type="text" maxLength="50"
                                   onChange={setParam}
                                   placeholder={'param' + (index + 1)}
                                   className="form-control"
                                   value={value}
                                   key={index}
                            />
                        )
                    })}
                </div>
            </div>
        )
    }, [t, state.metaParams, setParam, getMetaParamValue]);

    const renderSelfieLink = useCallback(() => {
        if (!state.link || !state.link.selfie_link) {
            return null;
        }

        return (
            <React.Fragment>
                {/* <div className="hr-line-dashed" /> */}
                <FormGroup
                    label={'Selfie link'}
                >
                    <input
                        type="text"
                        maxLength="255"
                        value={state.selfieLink || ''}
                        onChange={(e) => setSelfieLink(e.target.value)}
                        placeholder="Selfie link"
                        className="form-control"
                    />
                </FormGroup>
            </React.Fragment>
        );
    }, [state.link, state.selfieLink, setSelfieLink]);

    const onErrorMsg = useCallback((msg) => {
        CustomNotify(staticNames[msg] || msg, 'error');
    }, [staticNames]);

    const onCopyButtonClick = useCallback(() => {
        const code = getCode();
        if (!code || !code.trim()) {
            CustomNotify(staticNames.cantCreateUrlForCreativeNoLinksAvailable || 'No link to copy', 'error');
            return;
        }

        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(code).then(() => {
                CustomNotify(staticNames.copyLinkBuffer || staticNames.copy || 'Link copied to clipboard');
            }).catch(() => {
                try {
                    const tmp = document.createElement('textarea');
                    tmp.value = code;
                    document.body.appendChild(tmp);
                    tmp.select();
                    document.execCommand('copy');
                    document.body.removeChild(tmp);
                    CustomNotify(staticNames.copyLinkBuffer || staticNames.copy || 'Link copied to clipboard');
                } catch (e) {
                    CustomNotify(staticNames.copyError || 'Copy failed', 'error');
                }
            });
        } else {
            try {
                const tmp = document.createElement('textarea');
                tmp.value = code;
                document.body.appendChild(tmp);
                tmp.select();
                document.execCommand('copy');
                document.body.removeChild(tmp);
                CustomNotify(staticNames.copyLinkBuffer || staticNames.copy || 'Link copied to clipboard');
            } catch (e) {
                CustomNotify(staticNames.copyError || 'Copy failed', 'error');
            }
        }

        const saveFn = typeof propOnSave === 'function' ? propOnSave : (typeof saveLinkPresets === 'function' ? saveLinkPresets : null);
        if (saveFn) {
            saveFn(state, t || {}, () => {}, () => {}, { notify: false });
        }
    }, [getCode, staticNames, propOnSave, saveLinkPresets, state, t]);

    const setSelfieLink = useCallback((link) => {
        setState(prevState => ({
            ...prevState,
            selfieLink: link
        }));
    }, []);

    const onGoalChange = useCallback((e) => {
        const goalId = e.target.value;
        setState(prevState => ({
            ...prevState,
            goalEntryId: goalId || null
        }));
    }, []);

    useEffect(() => {
        initLink();
    }, [initLink]);

    useEffect(() => {
        getShortenerDomainListHandler();
    }, [getShortenerDomainListHandler]);

    useEffect(() => {
        const isChangeLinkId = state.linkId !== linkId;
        const isModalOpening = visible && !state.modalIsVisible;

        setState(prevState => {
            const newGoalEntryId = isModalOpening
                ? (goalEntryId || getFirstGoal())
                : (prevState.goalEntryId || goalEntryId || getFirstGoal());

            const shouldClearParams = isModalOpening || isChangeLinkId;

            return {
                ...prevState,
                creativeType: creativeType || 'link',
                creativeId: creativeId || linkId,
                goalEntries: goalEntries,
                publisherId: publisherId,
                linkId: linkId,
                type: type,
                selfieLink: shouldClearParams ? '' : prevState.selfieLink,
                codeParams: codeParams || {},
                access: !!getFirstGoal() && access,
                modalIsVisible: visible,
                goalEntryId: newGoalEntryId,
                metaParams: shouldClearParams ? {} : prevState.metaParams
            };
        });

        if (isChangeLinkId) {
            initLink();
        }
    }, [linkId, creativeType, creativeId, goalEntries, publisherId, type, codeParams, access, visible, getFirstGoal, initLink]);

    useEffect(() => {
        if (state.shortUrl.length >= 4) {
            const params = {
                url: state.shortUrl,
                size: parseInt(state.qrCodeSize),
                type: state.qrCodeType,
                colors: JSON.stringify({
                    colorR: state.qrCodeColors.colorRed ? parseInt(state.qrCodeColors.colorRed) : 0,
                    colorG: state.qrCodeColors.colorGreen ? parseInt(state.qrCodeColors.colorGreen) : 0,
                    colorB: state.qrCodeColors.colorBlue ? parseInt(state.qrCodeColors.colorBlue) : 0,
                    backgroundR: state.qrCodeColors.bgColorRed ? parseInt(state.qrCodeColors.bgColorRed) : 255,
                    backgroundG: state.qrCodeColors.bgColorGreen ? parseInt(state.qrCodeColors.bgColorGreen) : 255,
                    backgroundB: state.qrCodeColors.bgColorBlue ? parseInt(state.qrCodeColors.bgColorBlue) : 255
                })
            };
            getQRCode(params, successQRCode);
        }
    }, [state.shortUrl, getQRCode, successQRCode]);

    useEffect(() => {
        if (state.qrCodeType && state.shortUrl.length >= 4) {
            const params = {
                url: state.shortUrl,
                size: parseInt(state.qrCodeSize),
                type: state.qrCodeType,
                colors: JSON.stringify({
                    colorR: state.qrCodeColors.colorRed ? parseInt(state.qrCodeColors.colorRed) : 0,
                    colorG: state.qrCodeColors.colorGreen ? parseInt(state.qrCodeColors.colorGreen) : 0,
                    colorB: state.qrCodeColors.colorBlue ? parseInt(state.qrCodeColors.colorBlue) : 0,
                    backgroundR: state.qrCodeColors.bgColorRed ? parseInt(state.qrCodeColors.bgColorRed) : 255,
                    backgroundG: state.qrCodeColors.bgColorGreen ? parseInt(state.qrCodeColors.bgColorGreen) : 255,
                    backgroundB: state.qrCodeColors.bgColorBlue ? parseInt(state.qrCodeColors.bgColorBlue) : 255
                })
            };
            getQRCode(params, successQRCode);
        }
    }, [state.qrCodeType, state.shortUrl, getQRCode, successQRCode]);

    useEffect(() => {
        return () => {
            if (qrColorChangeTimeoutRef.current) {
                clearTimeout(qrColorChangeTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (state.modalIsVisible) {
            updateQrSizeOutput(state.qrCodeSize);
        }
    }, [state.qrCodeSize, state.modalIsVisible, updateQrSizeOutput]);

    useEffect(() => {
        if (!state.modalIsVisible) {
            return;
        }

        const storedEntry = getStoredMetaEntry();
        if (!storedEntry.hasEntry) {
            return;
        }
        const storedMetaParams = storedEntry.params || {};

        setState(prevState => {
            const linkParams = normalizeParams(prevState.link && prevState.link.params);
            if (hasAllowedMetaParams(prevState.metaParams) || hasAllowedMetaParams(linkParams)) {
                return prevState;
            }
            const allowedMetaParams = pickAllowedMetaKeys(storedMetaParams);
            return {
                ...prevState,
                metaParams: { ...allowedMetaParams },
                link: prevState.link
                    ? {
                        ...prevState.link,
                        params: {
                            ...linkParams,
                            ...allowedMetaParams
                        }
                    }
                    : prevState.link
            };
        });
    }, [state.modalIsVisible, normalizeParams, getStoredMetaEntry, pickAllowedMetaKeys, hasAllowedMetaParams]);

    useEffect(() => {
        if (!state.modalIsVisible) {
            return;
        }

        if (prevPublisherIdRef.current === publisherId) {
            return;
        }

        prevPublisherIdRef.current = publisherId;
        const storedEntry = getStoredMetaEntry();
        const allowedMetaParams = pickAllowedMetaKeys(storedEntry.params);

        setState(prevState => {
            const linkParams = normalizeParams(prevState.link && prevState.link.params);
            return {
                ...prevState,
                metaParams: { ...allowedMetaParams },
                link: prevState.link
                    ? {
                        ...prevState.link,
                        params: {
                            ...removeAllowedMetaKeys(linkParams),
                            ...allowedMetaParams
                        }
                    }
                    : prevState.link
            };
        });
    }, [publisherId, state.modalIsVisible, normalizeParams, getStoredMetaEntry, pickAllowedMetaKeys, removeAllowedMetaKeys]);

    useEffect(() => {
        prevModalVisibleRef.current = state.modalIsVisible;
    }, [state.modalIsVisible]);


    if(undefined === state.modalOptions[state.type]) {
        return '';
    }

    if ("object" === typeof state.link && 0 === Object.keys(state.link).length && state.modalIsVisible) {
        return '';
    }
    if (undefined === state.link && state.modalIsVisible) {
        CustomNotify(staticNames.cantCreateUrlForCreativeNoLinksAvailable, 'error');
        return '';
    }

    let options = state.modalOptions[state.type];
    let qrCodeTypes = getQRCodeTypes();

    return (
        <div className="link-construrtor">
            {!state.access
                ? <CustomModal
                    visible={state.modalIsVisible}
                    onClose={onClose}
                    onConfirm={onClose}
                    confirmName={staticNames.confirm}
                    title={options.title}
                    areNoControls={true}
                >
                    <div className="row row-line">
                        <div className="col-sm-12">
                            <span>{staticNames.getAccess}.</span>
                        </div>
                    </div>
                </CustomModal>
                : <CustomModal
                    visible={state.modalIsVisible}
                    onClose={onClose}
                    onCancel={onClose}
                    onConfirm={onCopyButtonClick}
                    areNoControls={true}
                    showCancelButton={true}
                    confirmName={staticNames.saveAndCopyLink || staticNames.copy || 'Save and Copy Link'}
                    bsSize={'sm'}
                    title={options.title}
                >
                    <div className="form-row">
                        <h2>{staticNames.createLink}</h2>
                    </div>
                    {renderGoalEntries()}
                    {renderParams('link')}
                    {renderSubMetaParams('link')}
                    {state.link.selfie_link ? renderSelfieLink() : null}

                    <FormGroup>
                        {state.link && state.link.id ? (
                            <div className="form-row">
                                <label className="control-label bold mb2">{staticNames.result}{renderBadge()}</label>
                                <textarea
                                    className="form-control"
                                    value={getCode()}
                                    ref={resultRef}
                                    readOnly={true}
                                />
                            </div>
                        ) : null}
                        <div className="form-row">
                            <button
                                className='apply-btn'
                                type={'button'}
                                style={{margin: 0, width: '100%'}}
                                onClick={onCopyButtonClick}
                            >
                                {staticNames.copy}
                            </button> 
                        </div>
                    </FormGroup>

                    <hr></hr>

                    <FormGroup>
                        <div className="form-row">
                            <h2>{staticNames.blockShortLinkAndQR}</h2>
                        </div>
                        <div className="form-row">
                            <div style={{ width: '50%'}}>
                                <label className="label-domain">
                                    {staticNames.chooseDomain}
                                </label>
                                <select
                                    onChange={onSelectChange}
                                    className="form-control"
                                    name='domain'
                                >
                                    {domains.map((option, i) => (
                                        <option key={i} value={option.value}>{option.title}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ width: '50%'}}>
                                <div>
                                    <label className="label-ttl">
                                        {staticNames.ttlTitle}
                                    </label>
                                    <input
                                        className="form-control control-ttl"
                                        type="text"
                                        name='ttl'
                                        value={state.ttl}
                                        onChange={handleTtlChange}
                                    />
                                </div>
                                <p className="info-ttl" aria-required={true}>
                                    <span className="info-star-ttl">*</span>
                                    {staticNames.ttlMessage}
                                </p>
                            </div>
                        </div>
                        <button className='btn_short-link' onClick={getShortUrlHandler}>
                            {staticNames.shortLinkBtn}
                        </button>
                        <div className="form-row short-link_container">
                            <label>{staticNames.resultShortLink}</label>
                            <a className="control-item copy-short-link"
                               onClick={copyToClipboard}
                            >
                                <span className="fa fa-copy"></span>
                            </a>
                            <textarea
                                id="short-link_clip"
                                className="form-control"
                                value={state.shortUrl}
                                ref={shortLinkRef}
                                readOnly={true}
                                rows={1}
                            >
                            </textarea>
                        </div>
                        <div className="form-row qrcode_container">
                            <div className="form-row qrcode_group qrcode m-b" id="qrCode"
                                 dangerouslySetInnerHTML={{ __html: state.qrCodeContent }}
                            >
                            </div>
                            <label className="control-label bold">
                                {staticNames.qrCodeColors}
                            </label>
                            <div className="form-row qrcode_rgb-color">
                                <label className="control-label">
                                    {staticNames.qrCodeMainColor}
                                </label>
                                <div className="form-row qrcode_group m-b">
                                    <input
                                        className="form-control qrcode-control"
                                        type="number"
                                        name='colorRed'
                                        onChange={handleQRColorChange}
                                        placeholder={staticNames.qrCodeColorRed}
                                        value={state.qrCodeColors.colorRed}
                                    />
                                    <input
                                        className="form-control qrcode-control"
                                        type="number"
                                        name='colorGreen'
                                        onChange={handleQRColorChange}
                                        placeholder={staticNames.qrCodeColorGreen}
                                        value={state.qrCodeColors.colorGreen}
                                    />
                                    <input
                                        className="form-control qrcode-control"
                                        type="number"
                                        name='colorBlue'
                                        onChange={handleQRColorChange}
                                        placeholder={staticNames.qrCodeColorBlue}
                                        value={state.qrCodeColors.colorBlue}
                                    />
                                </div>
                                <label className="control-label">
                                    {staticNames.qrCodeBgColor}
                                </label>
                                <div className="form-row qrcode_group m-b">
                                    <input
                                        className="form-control qrcode-control"
                                        type="number"
                                        name='bgColorRed'
                                        onChange={handleQRColorChange}
                                        placeholder={staticNames.qrCodeColorRed}
                                        value={state.qrCodeColors.bgColorRed}
                                    />
                                    <input
                                        className="form-control qrcode-control"
                                        type="number"
                                        name='bgColorGreen'
                                        onChange={handleQRColorChange}
                                        placeholder={staticNames.qrCodeColorGreen}
                                        value={state.qrCodeColors.bgColorGreen}
                                    />
                                    <input
                                        className="form-control qrcode-control"
                                        type="number"
                                        name='bgColorBlue'
                                        onChange={handleQRColorChange}
                                        placeholder={staticNames.qrCodeColorBlue}
                                        value={state.qrCodeColors.bgColorBlue}
                                    />
                                </div>
                            </div>
                            <label className="control-label bold">
                                {staticNames.qrCodeSize}
                            </label>
                            <div className="form-row qrcode_size qrcode_group m-b">
                                <input
                                    id="sizeRange"
                                    className="control-qr_size"
                                    type="range"
                                    name='qrcodeSize'
                                    value={state.qrCodeSize}
                                    min={32}
                                    max={320}
                                    onChange={handleQRSizeChange}
                                    onMouseUp={getQRSizeChange}
                                />
                                <output id="sizeValue">{state.qrCodeSize}</output>
                            </div>
                            <div className="form-row qrcode_type qrcode_group">
                                <select
                                    onChange={handleQRTypeChange}
                                    className="form-control qrcode-control"
                                    name='qrcodeType'
                                >
                                    {qrCodeTypes.map((option, i) => (
                                        <option key={i} value={option.value}>{option.title}</option>
                                    ))}
                                </select>
                                <button id="qrCodeDownload" className='btn_qr-download disabled'
                                        dangerouslySetInnerHTML={{ __html: state.qrDownloadContent }}
                                >
                                </button>
                            </div>
                        </div>
                    </FormGroup>
                </CustomModal>
            }
        </div>
    );
};

LinkConstructor.propTypes = {
    creativeType: PropTypes.string,
    creativeId: PropTypes.string,
    linkId: PropTypes.string,
    visible: PropTypes.bool,
    goalEntries: PropTypes.array,
    publisherId: PropTypes.string,
    publisherIsReferral: PropTypes.bool,
    offer: PropTypes.object,
    goalEntryId: PropTypes.string,
    type: PropTypes.string,
    codeParams: PropTypes.object,
    access: PropTypes.bool,
    onClose: PropTypes.func,
    getDataLink: PropTypes.func,
    getShortUrl: PropTypes.func,
    getQRCode: PropTypes.func,
    saveLinkPresets: PropTypes.func,
    t: PropTypes.object
};

LinkConstructor.defaultProps = {
    creativeType: 'link',
    visible: false,
    goalEntries: [],
    publisherIsReferral: false,
    type: 'link',
    codeParams: {},
    access: true,
    onClose: () => {},
    getDataLink: () => {},
    getShortUrl: () => {},
    getQRCode: () => {},
    saveLinkPresets: () => {}
};

export default withTranslation(LinkConstructor);
